// Implementation taken from https://github.com/jd1378/deno-semaphore
// MIT License

/**
 * A semaphore implementation for limiting concurrent async operations.
 *
 * Example:
 * ```ts
 * const semaphore = new Semaphore(10);
 * semaphore.use(async () => {
 *   // do something...
 * });
 * ```
 */
export class Semaphore {
  private tasks: Array<() => void> = [];
  count: number;

  constructor(count: number) {
    this.count = count;
  }

  /** returns current scheduled tasks length */
  get length(): number {
    return this.tasks.length;
  }

  async use<T>(fn: () => Promise<T>) {
    const release = await this.acquire();
    try {
      return await fn();
    } finally {
      release();
    }
  }

  /**
   * @returns a new, wrapped version of the function that will be limited by this semaphore.
   */
  wrap<T extends (...args: any[]) => Promise<any>>(fn: T): T {
    return ((...args: Parameters<T>) => this.use(() => fn(...args))) as T;
  }

  // We could make this public, but it's not clear to me why we'd ever choose to use
  // this method over the  `use` method, which provides automatic error handling.
  private acquire() {
    return new Promise<() => void>((resolve) => {
      const task = () => {
        let released = false;
        resolve(() => {
          if (!released) {
            released = true;
            this.count++;
            this.schedule();
          }
        });
      };
      this.tasks.push(task);
      queueMicrotask(this.schedule.bind(this));
    });
  }

  private schedule() {
    if (this.count > 0 && this.tasks.length > 0) {
      this.count--;
      const next = this.tasks.shift();
      if (next === undefined) {
        throw "Unexpected undefined value in tasks list";
      }
      next();
    }
  }
}
